import { useState, useEffect, useRef } from 'react'
import api from '@/api'

/**
 * useJob — polls a background job until it reaches a terminal state.
 *
 * The Trialflare API runs long-running operations (AI stage generation, large
 * data exports, etc.) as background jobs. The caller kicks off the operation
 * via a POST endpoint that immediately returns { jobId }, then polls
 * GET /jobs/<jobId> until the job is done or has errored.
 *
 * This hook encapsulates that polling loop so components don't have to manage
 * the setInterval / clearInterval / unmount-cleanup boilerplate themselves.
 *
 * --- Basic usage ---
 *
 *   const { startPolling, isPolling } = useJob()
 *
 *   function doThing () {
 *     setLoading(true)
 *     api.trials.createSomeJob(trialId, ({ jobId }) => {
 *       startPolling(jobId, {
 *         onDone: result => {
 *           // result is job.result as returned by the API
 *           setLoading(false)
 *         },
 *         onError: message => {
 *           utils.toast('error', 'Something went wrong', message)
 *           setLoading(false)
 *         }
 *       })
 *     }, err => {
 *       // Handle failure to even create the job
 *       utils.toast('error', err.message)
 *       setLoading(false)
 *     })
 *   }
 *
 * --- isPolling ---
 *
 *   `isPolling` is true from the moment startPolling is called until the job
 *   reaches a terminal state (done or error). You can use it to drive loading
 *   spinners or disable buttons:
 *
 *   <Button loading={isPolling}>Generate</Button>
 *
 *   Note: there is a brief window between the user triggering an action and
 *   startPolling being called (while the job-creation POST is in flight) where
 *   isPolling is still false. If you need a spinner during that window too,
 *   manage your own loading state and set it to true before the API call:
 *
 *   setLoading(true)
 *   api.trials.createSomeJob(..., ({ jobId }) => startPolling(jobId, { ... }))
 *   // then setLoading(false) inside onDone / onError
 *
 * --- Unmount safety ---
 *
 *   The polling interval is always cleared when the component unmounts, so it
 *   is safe to use inside modals or other components that may disappear before
 *   the job finishes.
 *
 * --- Options ---
 *
 *   useJob({ intervalMs: 2000 })   // default poll interval; override if needed
 */
export default function useJob ({ intervalMs = 2000 } = {}) {
  const [isPolling, setIsPolling] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [])

  function stop () {
    clearInterval(intervalRef.current)
    intervalRef.current = null
    setIsPolling(false)
  }

  function startPolling (jobId, { onDone, onError }) {
    setIsPolling(true)
    intervalRef.current = setInterval(() => {
      api.trials.getJob(jobId, job => {
        if (job.status === 'done') {
          stop()
          onDone(job.result)
        } else if (job.status === 'error') {
          stop()
          onError(job.error)
        }
        // 'pending' — keep polling
      }, err => {
        // Network / auth failure on the poll request itself
        stop()
        onError(err.message)
      })
    }, intervalMs)
  }

  return { startPolling, isPolling }
}
